/**
 * The Data area — ADR-127 D1/D2/D3/D4, the fleet-level peer to Resources.
 *
 * WHAT THIS IS, AND WHY IT DID NOT EXIST BEFORE
 * --------------------------------------------
 * Every part below already existed and was tested: the schema-generated form (`DataSetForm`),
 * the Used-by panel (`DataUsedByPanel`), the immutable document writer (`dataDocumentEdit`),
 * the in-browser validator (`schemaValidator`), the version badges (`dataPinBadge`). NONE of
 * them had an importer, and there was no `/fleet-editor/data` route, so an operator could not
 * reach any of it. This module is the host that makes the area exist, and it deliberately adds
 * as little logic of its own as possible — the parts that were already proven stay in charge.
 *
 * THREE RULES THIS SURFACE MUST NOT SOFTEN
 * ---------------------------------------
 *  1. THE FORM MODEL READS RAW, VALIDATION READS COMPILED. `DataSetForm` takes both and the
 *     distinction is load-bearing: layer 2 lives in the raw `x-` keys, and its EFFECTS only
 *     exist once `compile_schema()` has materialised them. Getting it backwards silently drops
 *     every layer-2 obligation, which is why the server sends both and this page passes both
 *     through untouched.
 *  2. READ-ONLY IS NOT ONE STATE. A set can be un-editable because it is core-owned, because
 *     it lives in an integration, or because the server could not compile its schema. The
 *     first two are ownership facts with a ROUTE that works; the third is a degradation. They
 *     are rendered differently, because "you cannot edit this here, do it there" and "we could
 *     not load enough to let you edit safely" are different sentences.
 *  3. AN UNCHECKED IMPACT IS NOT A PASS. The impact endpoint answers a 503 with NO
 *     `blocks_propose` field rather than `false`, so that a caller cannot read "nothing would
 *     break" out of "we could not check". This page honours that: with no verdict, Save is
 *     disabled until the operator explicitly acknowledges proposing unchecked, and the
 *     acknowledgement is written into the PR message so the record says so.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api, dataSetTarget, type DataImpactVerdict, type DataSetDetail, type DataSetRow,
  type ProposeSkillResult } from '../api/client'
import { DeletePanel } from '../components/DeletePanel'
import { BreakingConsumerList } from './BreakingConsumerList'
import { dataSetPath } from './fleetEditorArea'
import { DataSetForm } from './DataSetForm'
import { DataUsedByPanel } from './DataUsedByPanel'
import { serializeDocument } from './dataDocumentEdit'
import { SchemaAddFields } from './SchemaAddFields'
import { SchemaBuilder } from './SchemaBuilder'
import { SourceBuilder } from './SourceBuilder'
import { ProposeToCoreAction } from './ProposeToCoreAction'
import { PendingConsumerList } from './PendingConsumerList'
import { validateAgainstCompiled, registryInstance, summarizeFindings } from './schemaValidator'

const CLASS_LABEL: Record<DataSetRow['klass'], string> = {
  fleet_owned: 'fleet-owned',
  core_owned: 'core-owned',
  external_fleet_instance: 'external',
  external_shared_instance: 'external, shared',
}

export interface DataAreaProps {
  canPropose: boolean
  /** The set named in the URL (/fleet-editor/data/<ref>), opened once the list
   *  has loaded — ADR-111 IA-6. A data set is referenced by many agents, so "the
   *  one we mean" is the most link-worthy thing in this area. */
  initialSet?: string
}

/**
 * Which kind of set `＋ New` is creating. The two differ in ONE thing that decides everything
 * else: whether this repo stores the values. A stored set's data document holds records; an
 * external set's holds only the contract (ADR-131 D2 / S8 E3). So the choice is offered up front
 * rather than as a toggle inside one form, because switching mid-authoring would mean discarding
 * either the records or the projection.
 */
type NewKind = 'stored' | 'external'

export function DataArea({ canPropose, initialSet }: DataAreaProps) {
  const navigate = useNavigate()
  // Collapsing the set list, mirroring FleetEditor's Agents rail.
  const [listCollapsed, setListCollapsed] = useState(false)
  const [building, setBuilding] = useState<NewKind | null>(null)
  const [rows, setRows] = useState<DataSetRow[] | null>(null)
  const [listNote, setListNote] = useState<string | null>(null)
  const [listError, setListError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)

  const [detail, setDetail] = useState<DataSetDetail | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [draft, setDraft] = useState<unknown>(null)
  const [rawMode, setRawMode] = useState(false)
  const [rawText, setRawText] = useState('')
  const [rawError, setRawError] = useState<string | null>(null)

  const [impact, setImpact] = useState<DataImpactVerdict | null>(null)
  const [impactUnavailable, setImpactUnavailable] = useState<string | null>(null)
  const [ackUnchecked, setAckUnchecked] = useState(false)
  // The browser validator's refusal, acknowledged. Separate from `ackUnchecked`: that one
  // answers "the impact gate could not run", this one answers "Ajv says no and Python may
  // not agree". Conflating them would let an acknowledgement of one carry the other.
  const [ackSchemaRefused, setAckSchemaRefused] = useState(false)
  const [saving, setSaving] = useState(false)
  // The WHOLE result, not a rendered string. Registration is a separate step after the pull
  // request is already open, so the response carries a warning this panel has to show; keeping
  // only a formatted "PR #N" threw that away and made a failed registration look like a clean one.
  const [saved, setSaved] = useState<ProposeSkillResult | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    api.getDataSets()
      .then(res => {
        setRows(res.sets ?? [])
        setListNote(res.note ?? null)
      })
      .catch(err => setListError(err instanceof Error ? err.message : String(err)))
  }, [])

  // --- Deep linking (ADR-111 IA-6) ------------------------------------------
  //
  // replace: focusing a set is not a distinct "page", and pushing would make
  // browser Back walk the operator through every row they clicked.
  const syncURL = (setRef: string) => navigate(dataSetPath(setRef), { replace: true })

  /** Focus a set, and say so in the URL. */
  const selectSet = (setRef: string) => {
    setSelected(setRef)
    syncURL(setRef)
  }

  // Land on a set, so the area is never an empty panel when it has content — the
  // one named in the URL if it resolves, else the first.
  //
  // The URL is written for the FALLBACK too, which is the half that is easy to
  // miss and the reason this had to change at all. Landing on /fleet-editor/data
  // selected `rows[0]` and left the address bar naming only the AREA, so copying
  // it shared "whatever set sorts first" — which silently resolves to a different
  // set for the recipient the moment one is published ahead of it. A link that is
  // one publish away from meaning something else is not a shareable link.
  //
  // A ref that matches nothing is left in the address bar: the set may be staged
  // and not yet deployed, and rewriting the URL destroys the evidence of which
  // link was shared. The operator still lands on the first set rather than an
  // empty panel.
  const resolvedDeepLink = useRef(false)
  useEffect(() => {
    if (selected || !rows || rows.length === 0) return
    const linked = !resolvedDeepLink.current && initialSet && rows.some(r => r.set === initialSet)
    resolvedDeepLink.current = true
    if (linked) {
      setSelected(initialSet!)
      return
    }
    setSelected(rows[0].set)
    if (!initialSet) syncURL(rows[0].set)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selected, initialSet])

  const resetEdit = useCallback(() => {
    setImpact(null)
    setImpactUnavailable(null)
    setAckUnchecked(false)
    setAckSchemaRefused(false)
    setSaved(null)
    setSaveError(null)
    setRawError(null)
    setRawMode(false)
  }, [])

  useEffect(() => {
    if (!selected) return
    setDetail(null)
    setDetailError(null)
    setDraft(null)
    resetEdit()
    api.getDataSet(selected)
      .then(d => {
        setDetail(d)
        setDraft(d.document)
        setRawText(serializeDocument(d.document))
      })
      .catch(err => setDetailError(err instanceof Error ? err.message : String(err)))
  }, [selected, resetEdit])

  const dirty = useMemo(() => {
    if (!detail || draft === null) return false
    return serializeDocument(draft) !== serializeDocument(detail.document)
  }, [detail, draft])

  // An acknowledgement is about ONE set of findings. Edit the document and it must be earned
  // again, exactly as `ackUnchecked` is re-earned when the impact gate re-runs — otherwise a
  // tick on a small overflow silently carries a later, larger one past the gate.
  useEffect(() => { setAckSchemaRefused(false) }, [draft])

  // The gate re-runs on every change to the proposal, because a verdict about a document the
  // operator has since edited is a stale verdict, and a stale pass is the dangerous kind.
  useEffect(() => {
    if (!detail || !dirty) {
      setImpact(null)
      setImpactUnavailable(null)
      setAckUnchecked(false)
      return
    }
    let live = true
    setAckUnchecked(false)
    api.getDataImpact(detail.set, { data: draft })
      .then(v => {
        if (!live) return
        if (v.blocks_propose === undefined) {
          // NO VERDICT. Not a pass — see the module note.
          setImpact(null)
          setImpactUnavailable(v.error ?? 'the impact gate returned no verdict')
        } else {
          setImpact(v)
          setImpactUnavailable(null)
        }
      })
      .catch(err => {
        if (!live) return
        setImpact(null)
        setImpactUnavailable(err instanceof Error ? err.message : String(err))
      })
    return () => { live = false }
  }, [detail, draft, dirty])

  /**
   * ADR-168 D6 — a published data document's `$schema_ref` may never change.
   *
   * The backend refuses this at the propose endpoint, and that is the guarantee:
   * any caller can skip this screen. This is the half that says WHY before the
   * operator spends a round trip on it. A consumer pins the DATA document's
   * version and reaches its shape THROUGH this field, so repointing it moves the
   * shape every unchanged `@v1` pin resolves to. The route is to publish the next
   * version, not to swap the binding underneath the current one.
   *
   * Every difference counts, dropping the field included: absence is not the old
   * value. The comparison is on the serialized value so an object-valued ref (there
   * are none, but nothing forbids one) is compared by content rather than identity.
   */
  const schemaRefChanged = useMemo(() => {
    if (!detail || draft === null) return false
    const refOf = (doc: unknown) => {
      if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return undefined
      return JSON.stringify((doc as Record<string, unknown>).$schema_ref)
    }
    return refOf(draft) !== refOf(detail.document)
  }, [detail, draft])

  /**
   * THE DRAFT AGAINST ITS SCHEMA — checked here rather than only inside `DataSetForm`.
   *
   * `DataSetForm` validates what it renders, and `Import JSON` refuses a file that does not
   * satisfy the schema. "Edit as JSON" had neither: `rawError` is a PARSE error, so a document
   * that parsed but broke a schema bound went to propose unchallenged. That is how a 219-record
   * paste reached a set whose schema capped it at 32 — the first thing to enforce the bound was
   * the canonical validator in CI, which reported it by dumping all 219 records into a log.
   *
   * This does not move authority. ADR-129's canonical Python validator remains the one that
   * decides, and the propose endpoint still runs it. This is the same advisory Ajv pass the
   * form already makes, applied on the path that skipped it, so the operator learns in the
   * editor instead of in a pull request that cannot merge.
   *
   * A set whose schema would not compile yields no verdict and does not block: that set is
   * already read-only for exactly that reason (`data-no-compiled`), and inventing a refusal
   * here would refuse it twice with a worse sentence.
   *
   * VALIDATE THE SAME INSTANCE PYTHON DOES, which is NOT the document — see
   * `registryInstance`. A divergence in the PROJECTION is not an engine disagreement, it is
   * this screen asking a different question from the authority, so it is fixed rather than
   * acknowledged below.
   */
  const schemaVerdict = useMemo(() => {
    if (!detail?.compiled_schema || draft === null || rawError) return null
    return validateAgainstCompiled(detail.compiled_schema, registryInstance(draft))
  }, [detail, draft, rawError])

  /**
   * THE refusal this set carries, if any — the input to the shared hand-off (ADR-168 D10).
   *
   * Two layers can refuse, and they are not the same refusal. The VALUES layer speaks through
   * `editable` + `why` + `route` (a core-owned or external set). The CONTRACT layer speaks
   * through `schema_publish` (ADR-168 D2), and it can refuse a set whose values are perfectly
   * editable — `fleet-channels@v1` is fleet-owned with a core-owned schema. The values-layer
   * refusal is preferred when it has a route because it is the more specific of the two; the
   * contract verdict answers otherwise, including for `unavailable`, whose route is the fleet's
   * own readiness route rather than core's.
   */
  const refusal = useMemo(() => {
    if (!detail) return null
    if (!detail.editable && detail.route) {
      return { reason: detail.why, route: detail.route }
    }
    const publish = detail.schema_publish
    if (publish && publish.state !== 'allowed' && publish.route) {
      return { reason: publish.reason || null, route: publish.route }
    }
    return null
  }, [detail])

  /**
   * The two-layer label (ADR-168 D2), when the layers disagree.
   *
   * `values` is the classifier's own class label; `contract` says why the schema is not this
   * fleet's to extend. `unavailable` gets its own word — "we could not read it" is not "it
   * belongs to someone else", and the panel's readiness refusal says the rest.
   */
  const layers = useMemo(() => {
    const publish = detail?.schema_publish
    if (!detail || !publish || publish.state === 'allowed') return null
    if (!detail.editable) return null
    return {
      values: CLASS_LABEL[detail.klass],
      contract: publish.state === 'denied' ? 'organisation-wide' : 'not readable',
    }
  }, [detail])

  /**
   * ADR-168 D2's THREE gates on `Add fields`, and they are not interchangeable.
   *
   * `canPropose` is the operator. `klass` is ownership — ADR-147's ruling, not `editable`, which
   * is false for five reasons unrelated to who owns the contract. `schema_publish.state` is the
   * contract verdict, and only `denied` hides the control.
   *
   * `unavailable` KEEPS IT VISIBLE. Nobody has said this fleet may not publish here — the schema
   * could not be read, which is precisely the malformed set this action exists to repair. The
   * panel refuses on OPEN with the readiness route instead, because a control that vanishes on
   * the one set that needs it is the failure ADR-147's ruling exists to prevent. Parseability is
   * a readiness refusal, never a visibility condition.
   */
  const canAddFields = canPropose
    && detail?.klass === 'fleet_owned'
    && detail.schema_publish?.state !== 'denied'

  const blocked = impact?.blocks_propose === true
  const unchecked = impactUnavailable !== null
  const schemaInvalid = schemaVerdict !== null && !schemaVerdict.valid
  // Ajv could not COMPILE the schema, so nothing was checked. Rule 3 of this module's header
  // applies to it exactly as it does to the impact gate: no verdict is not a pass. It routes
  // through the same acknowledgement, but it must not be described as invalid data — that
  // sends the operator hunting through records for a fault that is in the schema.
  const schemaUncheckable = schemaVerdict !== null
    && schemaVerdict.findings.some(f => f.keyword === 'schema')
  const canSave = canPropose && !!detail?.editable && dirty && !saving
    && !blocked && (!unchecked || ackUnchecked) && !rawError && !schemaRefChanged
    && (!schemaInvalid || ackSchemaRefused)

  const onSave = async () => {
    if (!detail || !canSave) return
    setSaving(true)
    setSaveError(null)
    try {
      const notes = []
      if (unchecked) {
        notes.push('proposed WITHOUT an impact check — the gate could not be evaluated and ' +
          'the operator acknowledged it')
      }
      if (schemaInvalid) {
        // The record says the browser refused and the operator went on anyway, so a reviewer
        // reading the PR knows to expect the canonical validator's verdict rather than
        // wondering how this passed the editor. SUMMARIZED, never the whole list — see
        // `summarizeFindings` for what an unbounded note did to the propose call.
        notes.push(schemaUncheckable
          ? 'proposed WITHOUT a browser schema check — the schema could not be compiled here ' +
            'and the operator acknowledged it; the canonical validator decides: ' +
            summarizeFindings(schemaVerdict!.findings)
          : 'proposed OVER the browser validator\'s refusal, which the operator ' +
            'acknowledged — the canonical validator decides: ' +
            summarizeFindings(schemaVerdict!.findings))
      }
      const note = notes.length > 0 ? ` [${notes.join('] [')}]` : ''
      const res = await api.proposeFleetFile(
        detail.data_path,
        serializeDocument(draft),
        `data(${detail.set}): edit via the Data area${note}`)
      setSaved(res)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const applyRaw = (text: string) => {
    setRawText(text)
    try {
      // The whole document is the new value here, so no path-addressed writer is
      // involved — dataDocumentEdit's immutable helpers exist for the FORM's
      // field-level edits, and reaching for one here would be indirection.
      setDraft(JSON.parse(text))
      setRawError(null)
    } catch (err) {
      // The draft is deliberately NOT updated while the text is unparseable: a half-typed
      // document must not reach the form, the validator, or the impact gate.
      setRawError(err instanceof Error ? err.message : 'not valid JSON')
    }
  }

  if (listError) {
    return (
      <p className="text-sm text-red-700 p-6" data-testid="data-list-error">
        The set list could not be loaded: {listError}. This is not “this fleet has no reference
        data” — nothing is being claimed about what exists.
      </p>
    )
  }

  return (
    <div className="flex gap-6 min-h-0" data-testid="data-area">
      {/* Collapsed rail — the SAME gesture the Agents rail uses (FleetEditor's
          railCollapsed), because a second spelling for "hide this panel" is a
          thing to learn rather than a thing to recognise. The table is the widest
          thing in this app and the set list is the least often needed once a set
          is open. */}
      {listCollapsed && (
        <div
          className="w-10 shrink-0 flex flex-col items-center rounded-lg border border-gray-200 bg-white pt-2"
          data-testid="data-list-rail"
        >
          <button
            type="button"
            onClick={() => setListCollapsed(false)}
            title="Show data sets"
            aria-label="Show data sets"
            data-testid="data-list-expand"
            className="text-gray-500 hover:text-gray-800"
          >›</button>
          <span className="mt-2 text-[10px] text-gray-400" style={{ writingMode: 'vertical-rl' }}>
            Data sets
          </span>
        </div>
      )}
      {/* The set list (D1): every set, including the read-only ones. An operator reasoning
          about what a workflow can reference needs to see everything referenceable. */}
      {!listCollapsed && (
      <div className="w-72 shrink-0 border-r border-gray-100 pr-4 overflow-y-auto" data-testid="data-list-panel">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Data sets
          </h2>
          <div className="flex items-center gap-2">
          {canPropose && (
            <>
              <button
                type="button"
                onClick={() => setBuilding('stored')}
                data-testid="data-new-schema"
                className="text-xs text-indigo-700 hover:underline"
              >
                ＋ New
              </button>
              <button
                type="button"
                onClick={() => setBuilding('external')}
                data-testid="data-new-source"
                title="Bind a set to an integration — this repo stores the contract, not the values"
                className="text-xs text-indigo-700 hover:underline"
              >
                ＋ External
              </button>
            </>
          )}
            {/* Outside the canPropose gate: a VIEWER needs the room as much as an
                author does — collapsing is about screen space, not permission. */}
            <button
              type="button"
              onClick={() => setListCollapsed(true)}
              title="Hide data sets"
              aria-label="Hide data sets"
              data-testid="data-list-collapse"
              className="text-gray-400 hover:text-gray-700 leading-none"
            >‹</button>
          </div>
        </div>
        {listNote && (
          <p className="text-xs text-amber-700 mb-2" data-testid="data-list-note">{listNote}</p>
        )}
        {rows === null ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-400">
            No reference data in this fleet yet.
          </p>
        ) : (
          <ul>
            {rows.map(row => (
              <li key={row.set}>
                <button
                  type="button"
                  onClick={() => selectSet(row.set)}
                  aria-current={selected === row.set}
                  data-testid={`data-set-${row.set}`}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded ${
                    selected === row.set
                      ? 'bg-indigo-50 text-indigo-800 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <span className="block truncate">{row.set}</span>
                  <span className="block text-xs text-gray-400">
                    {CLASS_LABEL[row.klass]}
                    {row.status && row.status !== 'published' ? ` · ${row.status}` : ''}
                    {!row.editable ? ' · read-only' : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      )}

      <div className="flex-1 min-w-0 overflow-y-auto">
        {building === 'stored' ? (
          <SchemaBuilder
            canPropose={canPropose}
            onClose={() => setBuilding(null)}
            onCreated={() => {
              // The new set does not exist until the PR merges and deploys, so the list is NOT
              // optimistically updated — showing a row that is not there yet would invite an
              // edit against a document this fleet has not got.
              setBuilding(null)
            }}
          />
        ) : building === 'external' ? (
          /* ADR-135 D5 — the degraded mapping editor. Same gesture, same two-file changeset,
             and the same "not optimistically listed" rule for the same reason. */
          <SourceBuilder
            canPropose={canPropose}
            onClose={() => setBuilding(null)}
            onCreated={() => setBuilding(null)}
          />
        ) : detailError ? (
          <p className="text-sm text-red-700" data-testid="data-detail-error">{detailError}</p>
        ) : !detail ? (
          <p className="text-sm text-gray-400">{selected ? 'Loading…' : 'Select a data set.'}</p>
        ) : (
          <>
            <div className="mb-4">
              <h1 className="text-lg font-semibold text-gray-900">{detail.set}</h1>
              {/* EACH LABEL NAMES ITS OWN LAYER. `klass` is the SET's class and says nothing
                  about who owns the schema, so printed directly after the schema ref it read as
                  the schema's own ownership — and on `fleet-channels@v1`, whose values declare
                  `owner: fleet` and whose schema declares `owner: core`, it asserted the exact
                  opposite of the layer line three rows below. The class stays on this line: that
                  line renders only when the layers DISAGREE, so this is the detail pane's only
                  class label on an ordinary set. */}
              <p className="text-xs text-gray-500" data-testid="data-detail-subtitle">
                set {CLASS_LABEL[detail.klass]} · schema <code>{detail.schema}</code>
                {detail.badges.map(b => (
                  <span key={b} className="ml-2 px-1.5 py-0.5 rounded bg-gray-100">{b}</span>
                ))}
              </p>
              <p className="text-xs text-gray-400 mt-1"><code>{detail.data_path}</code></p>
            </div>

            {/* THE TWO LAYERS, NAMED SEPARATELY (ADR-168 D2).
                A set can be this fleet's at the VALUES layer and core's at the CONTRACT layer —
                `fleet-channels@v1` is exactly that — and one "editable / read-only" verdict
                cannot say it. Shown only when the two layers actually differ: on a set whose
                contract is also the fleet's there is nothing to disambiguate, and a label that
                is always there is a label nobody reads. */}
            {layers && (
              <p className="mb-3 text-xs text-gray-600" data-testid="data-layers">
                <strong>Values: {layers.values}</strong> — editable here.{' '}
                <strong>Contract: <code>{detail.schema}</code> · {layers.contract}</strong> —
                read-only here.
              </p>
            )}

            {/* An ownership mismatch is SHOWN, never resolved here: CI refuses to publish such
                a document, and a hand-edited repo is exactly when an operator needs to see it. */}
            {detail.ownership_mismatch && (
              <p className="mb-3 text-sm text-red-700" data-testid="data-mismatch">
                {detail.ownership_mismatch}
              </p>
            )}

            {/* A DEGRADATION — we could not load enough to edit safely. Distinct from the
                ownership read-only below, which is a fact with a route that works. */}
            {detail.notes.length > 0 && (
              <ul className="mb-3 text-sm text-amber-800" data-testid="data-notes">
                {detail.notes.map(n => <li key={n}>{n}</li>)}
              </ul>
            )}

            {/* ADR-168 D10 — a route renders whenever one EXISTS, not only when the whole set
                is read-only. `fleet-channels@v1` is editable at the values layer and denied at
                the CONTRACT layer, so the old `!detail.editable` condition rendered nothing at
                all for it: no reason, no route. And the route is a CONTROL: a refusal whose
                only expression is prose is the failure ADR-121 D1 exists to prevent. */}
            {refusal ? (
              <ProposeToCoreAction
                set={detail.set}
                schema={detail.schema}
                klass={detail.klass}
                reason={refusal.reason}
                route={refusal.route}
                publishState={detail.schema_publish?.state}
              />
            ) : !detail.editable && detail.why ? (
              <p className="mb-3 text-sm text-gray-600" data-testid="data-readonly-why">
                {detail.why}
              </p>
            ) : null}

            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={() => setRawMode(m => !m)}
                data-testid="data-toggle-raw"
                className="text-xs text-indigo-700 hover:underline"
              >
                {rawMode ? 'Back to the form' : 'Edit as JSON'}
              </button>
              {dirty && <span className="text-xs text-amber-700">unsaved changes</span>}
            </div>

            {rawMode ? (
              /* ADR-121 D1 — the escape hatch. Always available, because a generated form
                 cannot express every edit and an operator must never be stuck. */
              <div>
                <textarea
                  value={rawText}
                  onChange={e => applyRaw(e.target.value)}
                  disabled={!detail.editable}
                  data-testid="data-raw"
                  rows={20}
                  spellCheck={false}
                  className="w-full font-mono text-xs border border-gray-200 rounded p-2"
                />
                {rawError && (
                  <p className="text-sm text-red-700" data-testid="data-raw-error">
                    {rawError} — the form and the impact check are not being updated while this
                    is unparseable.
                  </p>
                )}
              </div>
            ) : detail.compiled_schema ? (
              <DataSetForm
                schema={detail.schema_document}
                compiledSchema={detail.compiled_schema}
                value={draft}
                onChange={next => { setDraft(next); setRawText(serializeDocument(next)) }}
                // The registry path's basename, so an exported file round-trips under
                // the name the operator already sees on this page.
                exportName={(detail.data_path ?? 'data.json').split('/').pop()}
                readOnly={!detail.editable}
                readOnlyReason={detail.why ?? undefined}
                // THE ROUTE IS NOT PASSED DOWN. ADR-168 D10 requires the route to be a control,
                // and `ProposeToCoreAction` above renders exactly that on this same screen for
                // every refusal this set carries. Rendering it here as well put the same route on
                // the page twice, the second time as unclickable prose.
              />
            ) : (
              <p className="text-sm text-amber-800" data-testid="data-no-compiled">
                The generated form needs the compiled schema, which is unavailable — use
                “Edit as JSON” to read the document. Saving is disabled rather than offered
                without validation.
              </p>
            )}

            {/* THE BROWSER VALIDATOR'S REFUSAL, and the way past it.
                Ajv is not the authority — ADR-129's Python validator is, and it runs at propose.
                The two engines can disagree honestly: a `pattern` whose regex flags Ajv cannot
                compile, or a character class the two read differently. A HARD block here would
                turn every such disagreement into an edit nobody can make through Mission
                Control at all, which is a worse failure than the one this gate exists to stop.
                So it refuses loudly, names the findings, and lets the operator go on record.
                This mirrors `ackUnchecked` above: the acknowledgement is the control, and it is
                written into the PR message so a reviewer sees what was overridden. */}
            {schemaInvalid && (
              <div className="mb-3" data-testid="data-schema-refused">
                <p className="text-sm text-red-700">
                  {schemaUncheckable
                    ? 'This set’s schema could not be compiled here, so the document was NOT ' +
                      'checked in the browser. This is not “it is fine”:'
                    : 'This document does not satisfy the set’s schema, so it was not proposed:'}
                </p>
                <ul className="text-sm text-red-700 mt-1">
                  {schemaVerdict!.findings.map((f, i) => (
                    <li key={i}>
                      <code>{f.pointer || '(document)'}</code> {f.message}
                    </li>
                  ))}
                </ul>
                <label className="text-xs text-gray-700 inline-flex items-center gap-1 mt-1">
                  <input
                    type="checkbox"
                    checked={ackSchemaRefused}
                    onChange={e => setAckSchemaRefused(e.target.checked)}
                    data-testid="data-ack-schema-refused"
                  />
                  {schemaUncheckable
                    ? 'Propose anyway — the canonical validator decides, and the PR will ' +
                      'record that nothing was checked here'
                    : 'Propose anyway — the canonical validator decides, and the PR will ' +
                      'say this was overridden'}
                </label>
              </div>
            )}

            {schemaRefChanged && (
              <p className="mb-3 text-sm text-red-700" data-testid="data-schema-ref-locked">
                <code>$schema_ref</code> cannot be changed on a published data document
                (ADR-168 D6): a consumer pins this document’s version and reaches its shape
                through this field, so repointing it would move the shape every existing pin
                resolves to without changing one of those pins.{' '}
                <strong>Publish the next version of this set instead</strong> — the existing
                pins keep the shape and the values they were validated against.
              </p>
            )}

            {/* THE PROPOSE GATE (D3 layer 2) */}
            {dirty && (
              <div className="mt-4 border-t border-gray-100 pt-3">
                {blocked ? (
                  <div data-testid="data-impact-blocked">
                    <p className="text-sm text-red-700 font-medium">
                      This change breaks {impact?.breaking?.length ?? 0} declared consumer(s),
                      so it cannot be proposed.
                    </p>
                    {/* One rendering, shared with `Add fields` — see BreakingConsumerList for
                        the row's real shape and why an empty `lines` still says so. */}
                    <BreakingConsumerList
                      rows={impact?.breaking}
                      className="text-xs text-red-700 mt-1"
                    />
                    <PendingConsumerList rows={impact?.pending} className="text-xs mt-2" />
                  </div>
                ) : unchecked ? (
                  <div data-testid="data-impact-unchecked">
                    <p className="text-sm text-amber-800">
                      The impact gate could not be evaluated: {impactUnavailable}.{' '}
                      <strong>This is not “nothing would break”</strong> — no verdict was
                      reported.
                    </p>
                    <label className="text-xs text-gray-700 inline-flex items-center gap-1 mt-1">
                      <input
                        type="checkbox"
                        checked={ackUnchecked}
                        onChange={e => setAckUnchecked(e.target.checked)}
                        data-testid="data-ack-unchecked"
                      />
                      Propose anyway, and record in the PR that it was not checked
                    </label>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600" data-testid="data-impact-ok">
                      {impact?.consumers_checked ?? 0} declared consumer(s) checked, none broken.
                      {(impact?.warnings?.length ?? 0) > 0 &&
                        ` ${impact?.warnings?.length} warning(s).`}
                    </p>
                    {/* "None broken" is the whole of this branch's claim today, and a consumer
                        waiting on uncommitted data is not broken — it is unpublishable. The edit
                        stays proposable; the rows below are what has to land first. */}
                    <PendingConsumerList rows={impact?.pending} className="text-xs mt-2" />
                  </>
                )}
              </div>
            )}

            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={onSave}
                disabled={!canSave}
                data-testid="data-save"
                className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-40"
              >
                {saving ? 'Proposing…' : 'Propose change'}
              </button>
              {!canPropose && (
                <span className="text-xs text-gray-500">
                  Proposing needs the fleet-owner role.
                </span>
              )}
              {saved && (saved.improvement_warning
                ? <span className="text-xs text-amber-800" data-testid="data-saved-warning">
                    {saved.improvement_warning}
                  </span>
                : <span className="text-xs text-green-700" data-testid="data-saved">
                    Sent for review.{' '}
                    {saved.improvement_id
                      ? <Link to={`/improvements/${encodeURIComponent(saved.improvement_id)}/pr`}
                          className="font-medium underline">Review &amp; approve in Mission Control →</Link>
                      : <>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</>}
                  </span>
              )}
              {saveError && (
                <span className="text-xs text-red-700" data-testid="data-save-error">
                  {saveError}
                </span>
              )}
            </div>

            {/* ADD FIELDS (ADR-168). Its own block, above the retire block, because it is the
                other thing an operator does to the CONTRACT rather than to the values — and
                because the panel is a card once opened, not an inline control. */}
            {canAddFields && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Change the shape
                </p>
                <SchemaAddFields
                  set={detail.set}
                  schema={detail.schema}
                  klass={detail.klass}
                  schemaDocument={detail.schema_document}
                  publishState={detail.schema_publish?.state}
                  publishReason={detail.schema_publish?.reason}
                  publishRoute={detail.schema_publish?.route}
                />
              </div>
            )}

            {/* RETIRE THIS VERSION (ADR-147).

                THE GATE IS `klass`, NOT `editable`. Besides the ownership class,
                `editable` is false for five reasons that have nothing to do with
                who may retire the set (data_set_detail.go): paths discovery could
                not locate, a path resolving outside the fleet directory, a schema
                document that will not parse, a data document that will not parse,
                and an UNAVAILABLE COMPILED SCHEMA. A set whose schema
                will not compile is precisely the "created wrongly" case this action
                exists to remove, so gating on `editable` would hide the control in
                the case it was requested for. Ownership is the whole question, and
                `klass` is the classifier's own answer to it, already on the wire.

                `onProposed` is DELIBERATELY OMITTED. The set still exists until the
                PR merges and deploys, so refreshing the list or clearing the
                selection would assert a state the fleet has not reached — and it
                would unmount DeletePanel's own outcome the instant it appeared. The
                same rule SchemaBuilder's onCreated already follows, for the same
                reason.

                IT SITS BELOW THE ACTION ROW, NOT INSIDE IT.
                DeletePanel is a collapsed button until it is opened and a bordered
                BLOCK card afterwards — the blocked list, the Architect section, a
                confirmation input and two buttons. Mounted inside the flex row it
                laid the whole expanded flow out as one inline item squeezed beside
                "Propose change", and nested block elements inside a <span>. jsdom
                cannot see either problem, which is why it survived its own contract
                test. AgentProfileFacet gives the same component its own separated
                block for the same reason, with the same divider; this follows it. */}
            {canPropose && detail.klass === 'fleet_owned' && (
              <div className="mt-4 border-t-2 border-red-100 pt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Retire this version
                </p>
                <DeletePanel target={dataSetTarget(detail.set)} label="Delete data set" />
              </div>
            )}

            <div className="mt-6">
              <DataUsedByPanel set={detail.set} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
